import { ArrowUpRight, ChevronRight, Gauge } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API, SITE_ORIGIN, setJsonLd, removeJsonLd } from "@/lib/site";
import { ESTIMATE_NOTE, TUNING_CATEGORIES } from "@/lib/tuningCategories";
import { EstimatedBadge } from "@/pages/TuningCategoryIndex";

export default function TuningCategoryModel({ category }) {
  const cat = TUNING_CATEGORIES[category];
  const { make, model } = useParams();
  const [data, setData] = useState(null);
  const [miss, setMiss] = useState(false);

  useEffect(() => {
    setData(null);
    setMiss(false);
    axios
      .get(`${API}/tuning-database/categories/${category}/models/${make}/${model}`)
      .then((r) => setData(r.data))
      .catch(() => setMiss(true));
  }, [category, make, model]);

  // ---- SEO head ----
  useEffect(() => {
    if (!data) return;
    document.title = cat.modelTitle(data.make, data.model);
    document.querySelector('meta[name="description"]')?.setAttribute("content", cat.modelDesc(data.make, data.model, data.vehicle_count));
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_ORIGIN}${cat.basePath}/${data.make_slug}/${data.model_slug}`;
    setJsonLd("tcmod-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Tuning Database", item: `${SITE_ORIGIN}/tuning-database` },
        { "@type": "ListItem", position: 3, name: cat.name, item: `${SITE_ORIGIN}${cat.basePath}` },
        { "@type": "ListItem", position: 4, name: data.make, item: `${SITE_ORIGIN}${data.make_url}` },
        { "@type": "ListItem", position: 5, name: data.model, item: `${SITE_ORIGIN}${cat.basePath}/${data.make_slug}/${data.model_slug}` },
      ],
    });
    setJsonLd("tcmod-collection", {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${data.make} ${data.model} ECU Remapping & Tuning Specifications`,
      url: `${SITE_ORIGIN}${cat.basePath}/${data.make_slug}/${data.model_slug}`,
      isPartOf: { "@type": "WebSite", name: "Regal Tuning", url: `${SITE_ORIGIN}/` },
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: data.vehicle_count,
        itemListElement: data.variants.slice(0, 80).map((v, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: `${data.make} ${data.model} ${v.engine}`,
          url: `${SITE_ORIGIN}${v.url}`,
        })),
      },
    });
    return () => {
      removeJsonLd("tcmod-breadcrumb");
      removeJsonLd("tcmod-collection");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (miss) {
    return (
      <main className="pt-32 sm:pt-40 pb-24" data-testid="tcmod-notfound">
        <div className="container-regal">
          <div className="border border-line bg-surface p-10 text-center">
            <Gauge size={28} className="mx-auto text-regal-amber" strokeWidth={1.5} />
            <h1 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">Model not found</h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-neutral-400">That model isn't in our {cat.name.toLowerCase()} database yet — coverage grows continuously.</p>
            <Link to={`${cat.basePath}/${make}`} className="mt-6 inline-flex items-center gap-2 bg-regal-amber px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors" data-testid="tcmod-notfound-back">
              Back to manufacturer
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="pt-32 sm:pt-40 pb-24" data-testid="tcmod-loading">
        <div className="container-regal"><div className="h-96 animate-pulse border border-line bg-white" /></div>
      </main>
    );
  }

  const hasEstimated = data.variants.some((v) => v.data_status === "estimated");

  return (
    <main className="pt-32 sm:pt-40 pb-24" data-testid="tcmod-page">
      <div className="container-regal">
        <nav className="mb-6 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-regal-amber">Home</Link>
          <ChevronRight size={12} />
          <Link to="/tuning-database" className="hover:text-regal-amber">Tuning Database</Link>
          <ChevronRight size={12} />
          <Link to={cat.basePath} className="hover:text-regal-amber">{cat.name}</Link>
          <ChevronRight size={12} />
          <Link to={data.make_url} className="hover:text-regal-amber" data-testid="tcmod-breadcrumb-make">{data.make}</Link>
          <ChevronRight size={12} />
          <span className="text-neutral-300">{data.model}</span>
        </nav>

        <p className="overline-label mb-4">{cat.name} Model</p>
        <h1 className="max-w-4xl font-display text-3xl sm:text-5xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink" data-testid="tcmod-h1">
          {data.make} <span className="text-regal-amber">{data.model}</span> Tuning Specifications
        </h1>
        <p className="mt-5 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed" data-testid="tcmod-intro">
          {cat.modelDesc(data.make, data.model, data.vehicle_count)}
        </p>

        {/* VARIANTS TABLE */}
        <div className="mt-10 overflow-x-auto border border-line" data-testid="tcmod-variants">
          <table className="w-full min-w-[720px] bg-white text-left">
            <thead>
              <tr className="border-b border-line">
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500 sm:px-6">Variant</th>
                <th className="px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500 sm:px-6">Original</th>
                <th className="bg-regal-amber px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-white sm:px-6">Stage 1</th>
                <th className="px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-700 sm:px-6">Gain</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500 sm:px-6">ECU</th>
                <th className="px-4 py-3 sm:px-6"><span className="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {data.variants.map((v, i) => {
                const gain = v.original_bhp != null && v.stage1_bhp != null ? v.stage1_bhp - v.original_bhp : null;
                return (
                  <tr key={v.url} className="border-b border-line last:border-0" data-testid={`tcmod-variant-${i}`}>
                    <td className="px-4 py-4 sm:px-6">
                      <p className="font-display text-sm font-bold uppercase tracking-wide text-ink">{v.label}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">{[v.fuel].filter(Boolean).join(" · ")}</p>
                    </td>
                    <td className="px-4 py-4 text-center sm:px-6">
                      <p className="font-display text-xl font-extrabold text-neutral-400">{v.original_bhp ?? "—"}</p>
                      {v.original_nm != null && (
                        <p className="font-mono text-[10px] text-neutral-400">
                          {v.original_nm} Nm
                          {v.original_nm_estimated && <EstimatedBadge testId={`tcmod-orig-est-${i}`} />}
                        </p>
                      )}
                    </td>
                    <td className="bg-regal-amber/5 px-4 py-4 text-center sm:px-6">
                      <p className="font-display text-xl font-extrabold text-regal-amber">
                        {v.stage1_bhp ?? "—"}
                        {v.data_status === "estimated" && <EstimatedBadge testId={`tcmod-s1-est-${i}`} />}
                      </p>
                      {v.stage1_nm != null && <p className="font-mono text-[10px] text-neutral-400">{v.stage1_nm} Nm</p>}
                    </td>
                    <td className="px-4 py-4 text-center sm:px-6">
                      {gain != null ? <p className="font-display text-lg font-extrabold text-emerald-700">+{gain}</p> : <p className="text-neutral-300">—</p>}
                    </td>
                    <td className="px-4 py-4 sm:px-6">
                      <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-neutral-500">
                        {v.ecu ? `${v.ecu.maker ? `${v.ecu.maker} ` : ""}${v.ecu.designation}` : "—"}
                      </p>
                    </td>
                    <td className="px-4 py-4 text-right sm:px-6">
                      <Link to={v.url} className="inline-flex items-center gap-1 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-regal-amber hover:text-amber-400 transition-colors" data-testid={`tcmod-open-${i}`}>
                        Details <ArrowUpRight size={12} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {hasEstimated && category === "agriculture" && (
          <p className="mt-4 max-w-3xl text-xs leading-relaxed text-neutral-500" data-testid="tcmod-estimate-note">{ESTIMATE_NOTE}</p>
        )}
      </div>
    </main>
  );
}
